import * as Sentry from "@sentry/browser";
import {
  Button,
  Card,
  Col,
  Descriptions,
  FormInstance,
  Row,
  Spin,
  Tag,
  message,
} from "antd";
import moment from "moment";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import { AxiosResponse } from "axios";
import {
  formatSnakeCase,
  formatter,
  generateDisplayErrorMessage,
  getStateColor,
  handleValidationErrors,
  request,
  responseHandler,
} from "../helpers/utilities";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import {
  APIError,
  FixedIncomePaymentInstruction,
  SelectOptionsInterface,
  User,
} from "../helpers/shared-interfaces";
import {
  GET_FIXED_INCOME_PAYMENT_INSTRUCTION,
  UPDATE_FIXED_INCOME_PAYMENT_INSTRUCTION,
} from "../gql/fixed-income-requests";
import ModalCreator from "../components/ModalCreator";
import NewBaseForm from "../components/Form/BaseForm";
import debounce from "lodash.debounce";
import { USERS_BY_ROLE_QUERY } from "../gql/users";
import { DEBOUNCE_TIME } from "../helpers/constants";

interface MatchParams {
  id: string;
}

interface State {
  paymentInstruction: FixedIncomePaymentInstruction | null;
  isLoading: boolean;
  showPaymentInstructionModal: boolean;
  isEditingPaymentInstruction: boolean;
  paymentInstructionId?: string;
  selectedRate?: number;
  selectedTenure?: number;
  paymentInstructionReceiptUrl?: string;
  userSearchTerm: string;
  users: User[];
  isSearchingUser: boolean;
  currentUser?: User;
  amountsPaid: number[];
  offers: (SelectOptionsInterface & { rate?: number; tenure?: number })[];
}

export default class ViewFixedIncomePaymentInstruction extends React.Component<
  RouteComponentProps<MatchParams>,
  State
> {
  paymentInstructionReference: RefObject<FormInstance>;

  private _isMounted = false;

  constructor(props: RouteComponentProps<MatchParams>) {
    super(props);
    this.state = {
      paymentInstruction: null,
      isLoading: true,
      showPaymentInstructionModal: false,
      isEditingPaymentInstruction: false,
      userSearchTerm: "",
      users: [],
      isSearchingUser: false,
      currentUser: undefined,
      paymentInstructionId: undefined,
      selectedRate: undefined,
      selectedTenure: undefined,
      paymentInstructionReceiptUrl: undefined,
      amountsPaid: [0],
      offers: [],
    };

    this.paymentInstructionReference = React.createRef<FormInstance>();
  }

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    await this.getPaymentInstruction();
  }

  togglePaymentInstructionModal = (isEdit: boolean = true): void => {
    const { paymentInstruction, showPaymentInstructionModal } = this.state;

    if (!showPaymentInstructionModal) {
      const product = paymentInstruction?.fixedIncomeRequest?.product;
      const offers = (product?.fixedIncomeOffers || [])
        .filter((offer: any) => offer.isActive)
        .map((offer: any) => ({
          id: offer.id,
          name: `${offer.rate}% - ${offer.tenure} days`,
          value: offer.id,
          label: `${offer.rate}% - ${offer.tenure} days`,
          rate: offer.rate,
          tenure: offer.tenure,
        }));

      if (isEdit && paymentInstruction) {
        const pi = paymentInstruction;
        this.setState({
          offers,
          isEditingPaymentInstruction: false,
          paymentInstructionId: pi.id,
          amountsPaid: pi.amountsPaid || [0],
          selectedRate: pi.rate,
          selectedTenure: pi.tenure,
          paymentInstructionReceiptUrl: pi.receiptUrl,
          showPaymentInstructionModal: true,
          currentUser: pi.assignedTo || undefined,
        }, () => this.getAdvisors());

        setTimeout(() => {
          this.paymentInstructionReference.current?.setFieldsValue({
            assignedToId: pi.assignedTo?.id,
            amountsPaid: pi.amountsPaid || [0],
            rate: pi.rate,
            tenure: pi.tenure,
          });
        }, 0);
        return;
      } else {
        this.setState({
          offers,
          isEditingPaymentInstruction: false,
          showPaymentInstructionModal: true,
          selectedRate: undefined,
          selectedTenure: undefined,
          amountsPaid: [0],
          paymentInstructionId: undefined,
          paymentInstructionReceiptUrl: undefined,
        });
        return;
      }
    }

    this.setState({
      showPaymentInstructionModal: false,
      selectedRate: undefined,
      selectedTenure: undefined,
      amountsPaid: [0],
      isEditingPaymentInstruction: false,
      paymentInstructionId: undefined,
      paymentInstructionReceiptUrl: undefined,
    });
  };

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  getPaymentInstruction = async (): Promise<void> => {
    if (!this._isMounted) return;

    const { id } = this.props.match.params;

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: GET_FIXED_INCOME_PAYMENT_INSTRUCTION,
        variables: { id },
      });

      const paymentInstruction =
        response.data?.data?.fixedIncomePaymentInstruction;

      if (!paymentInstruction) {
        message.error("Payment instruction not found");
        this.props.history.push("/fixed-income-payment-instructions");
        return;
      }

      if (this._isMounted) {
        this.setState({
          paymentInstruction,
          paymentInstructionId: paymentInstruction.id,
          isLoading: false,
        });
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({ isLoading: false });
        message.error(`Failed to load payment instruction - ${generateDisplayErrorMessage(error)}`);
      }
      Sentry.captureException(error);
    }
  };

  updatePaymentInstruction = async (): Promise<void> => {
    if (!this.paymentInstructionReference.current) return;

    interface Response {
      updateFixedIncomePaymentInstruction: any;
      errors: APIError[];
    }

    const { paymentInstruction, paymentInstructionReceiptUrl } = this.state;

    if (!paymentInstruction?.id) {
      message.error("Payment instruction ID is missing");
      return;
    }

    let data;

    try {
      data = await this.paymentInstructionReference.current.validateFields([
        "assignedToId",
        "amountsPaid",
        "rate",
        "tenure",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isEditingPaymentInstruction: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_FIXED_INCOME_PAYMENT_INSTRUCTION,
        variables: {
          id: paymentInstruction.id,
          assignedToId: data.assignedToId,
          amountsPaid: data.amountsPaid,
          rate: data.rate,
          tenure: data.tenure,
          receiptUrl: paymentInstructionReceiptUrl,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateFixedIncomePaymentInstruction",
      );

      if (err || mutation?.errors?.length) {
        message.error(generateDisplayErrorMessage(err || mutation.errors));
        this.setState({ isEditingPaymentInstruction: false });
        return;
      }

      message.success("Payment instruction updated successfully!");

      this.setState({
        showPaymentInstructionModal: false,
        selectedRate: undefined,
        selectedTenure: undefined,
        amountsPaid: [0],
        isEditingPaymentInstruction: false,
        paymentInstructionId: undefined,
        paymentInstructionReceiptUrl: undefined,
      });

      this.paymentInstructionReference.current.resetFields();
      this.getPaymentInstruction();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isEditingPaymentInstruction: false });
    }
  };

  getAdvisors = (): void => {
    request({
      query: USERS_BY_ROLE_QUERY,
      variables: {
        page: 1,
        q: this.state.userSearchTerm,
        role: "Advisors",
      },
    })
      .then(({ data: { data } }: any): void => {
        const users = data.users.results;

        const { currentUser } = this.state;
        if (currentUser && !users.find((u: User) => u.id === currentUser.id)) {
          users.unshift(currentUser);
        }

        this.setState({ users, isSearchingUser: false });
      })
      .catch((error) => {
        this.setState({ isSearchingUser: false });
        Sentry.captureException(error);
      });
  };

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm, isSearchingUser: true }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAdvisors();
  }, DEBOUNCE_TIME);

  handleOfferSelect = (offerId: string | number): void => {
    const selectedOffer = this.state.offers.find(
      (offer) => offer.id === String(offerId),
    );

    if (selectedOffer) {
      this.setState({
        selectedRate: selectedOffer.rate,
        selectedTenure: selectedOffer.tenure,
      });

      this.paymentInstructionReference.current?.setFieldsValue({
        rate: selectedOffer.rate,
        tenure: selectedOffer.tenure,
      });
    }
  };

  handleRateChange = (value: number): void => {
    this.setState({ selectedRate: value });
  };

  handleTenureChange = (value: number): void => {
    this.setState({ selectedTenure: value });
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url,
    }));
  };

  handleAmountsPaidChange = (index: number, value: number): void => {
    const newAmountsPaid = [...this.state.amountsPaid];
    newAmountsPaid[index] = value;
    this.setState({ amountsPaid: newAmountsPaid });
  };

  addAmountPaid = (): void => {
    this.setState({
      amountsPaid: [...this.state.amountsPaid, 0],
    });
  };

  removeAmountPaid = (index: number): void => {
    if (this.state.amountsPaid.length > 1) {
      const newAmountsPaid = this.state.amountsPaid.filter(
        (_, i) => i !== index,
      );
      this.setState({ amountsPaid: newAmountsPaid });
    }
  };

  render(): JSX.Element | null {
    const {
      paymentInstruction,
      isLoading,
      showPaymentInstructionModal,
      isEditingPaymentInstruction,
    } = this.state;

    if (isLoading || !paymentInstruction) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      );
    }

    return (
      <Spin spinning={isLoading}>
        <Card>
          <>
            <Row>
              <Col span={24}>
                {paymentInstruction && (
                  <Descriptions
                    title="Payment Instruction Details"
                    extra={
                      <Row
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <EntitledSection
                          entitlement={entitlements.FixedIncomeRequests?.edit}
                        >
                          {paymentInstruction.state !== "verified" && (
                            <Button
                              className="base-button"
                              onClick={() =>
                                this.togglePaymentInstructionModal(true)
                              }
                            >
                              Edit
                            </Button>
                          )}
                        </EntitledSection>
                      </Row>
                    }
                  >
                    <Descriptions.Item label={<b>State</b>}>
                      {paymentInstruction.state ? (
                        <Tag
                          color={getStateColor(paymentInstruction.state)}
                          style={{ textTransform: "capitalize" }}
                        >
                          {formatSnakeCase(paymentInstruction.state)}
                        </Tag>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Customer</b>}>
                      {paymentInstruction.fixedIncomeRequest?.customer ? (
                        <NavLink
                          to={`/fixed-income-requests/${paymentInstruction.fixedIncomeRequest?.id}`}
                        >
                          {`${paymentInstruction.fixedIncomeRequest.customer.firstName}
                      ${paymentInstruction.fixedIncomeRequest.customer.lastName}`}
                        </NavLink>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Total Amount Paid</b>}>
                      {paymentInstruction.totalAmountPaid !== undefined
                        ? formatter.format(paymentInstruction.totalAmountPaid)
                        : "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Product</b>}>
                      {paymentInstruction.fixedIncomeRequest?.product?.name ||
                        "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Provider</b>}>
                      {paymentInstruction.fixedIncomeRequest?.provider ? (
                        <NavLink
                          to={`/providers/${paymentInstruction.fixedIncomeRequest.provider.id}`}
                        >
                          {paymentInstruction.fixedIncomeRequest.provider.name}
                        </NavLink>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Rate (%)</b>}>
                      {paymentInstruction.rate || "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Tenure (Days)</b>}>
                      {paymentInstruction.tenure || "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Assigned To</b>}>
                      {paymentInstruction.assignedTo?.name || "Unassigned"}
                    </Descriptions.Item>

                    {paymentInstruction.receiptUrl && (
                      <Descriptions.Item label={<b>Receipt</b>}>
                        <a
                          href={paymentInstruction.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Receipt
                        </a>
                      </Descriptions.Item>
                    )}

                    <Descriptions.Item label={<b>Fixed Income Request</b>}>
                      <NavLink
                        to={`/fixed-income-requests/${paymentInstruction.fixedIncomeRequest?.id}`}
                      >
                        View Request
                      </NavLink>
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Created On</b>}>
                      {paymentInstruction.createdAt
                        ? moment(
                            paymentInstruction.createdAt,
                            "YYYY-MM-DD HH:mm:ss",
                          ).format("llll")
                        : "N/A"}
                    </Descriptions.Item>

                    <Descriptions.Item label={<b>Updated On</b>}>
                      {paymentInstruction.updatedAt
                        ? moment(
                            paymentInstruction.updatedAt,
                            "YYYY-MM-DD HH:mm:ss",
                          ).format("llll")
                        : "N/A"}
                    </Descriptions.Item>

                    {paymentInstruction.amountsPaid &&
                      paymentInstruction.amountsPaid.length > 0 && (
                        <Descriptions.Item
                          label={<b>Payment Breakdown</b>}
                          span={3}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            {paymentInstruction.amountsPaid.map(
                              (amount, index) => (
                                <Tag
                                  key={index}
                                  color="blue"
                                  style={{ fontSize: "14px" }}
                                >
                                  Payment {index + 1}:{" "}
                                  {formatter.format(amount)}
                                </Tag>
                              ),
                            )}
                          </div>
                        </Descriptions.Item>
                      )}
                  </Descriptions>
                )}
              </Col>
            </Row>

            {showPaymentInstructionModal &&
              ModalCreator({
                title: "Edit Payment Instruction",
                showModal: showPaymentInstructionModal,
                toggleModal: () =>
                  this.setState({ showPaymentInstructionModal: false }),
                onOkay: this.updatePaymentInstruction,
                okText: "Update",
                okType: "primary",
                isLoading: isEditingPaymentInstruction,
                maskClosable: false,
                width: 600,
                inscription: (
                  <NewBaseForm
                    layout="vertical"
                    reference={this.paymentInstructionReference}
                    fieldsToHide={[]}
                    fields={[
                      {
                        id: "offerSelector",
                        name: "offerSelector",
                        inputType: "select",
                        rule: "string",
                        required: false,
                        placeholder: "Select an offer",
                        label: "Offers",
                        selectOptions: this.state.offers,
                        selectHandler: this.handleOfferSelect,
                        colConfig: { span: 24 },
                      },
                      {
                        id: "rate",
                        name: "rate",
                        inputType: "number",
                        rule: "number",
                        required: true,
                        placeholder: "Enter rate",
                        label: "Rate (%)",
                        initialValue: this.state.selectedRate,
                        onNumberChange: this.handleRateChange,
                        colConfig: { span: 11 },
                        elementStyle: { width: "100%" },
                      },
                      {
                        id: "tenure",
                        name: "tenure",
                        inputType: "number",
                        rule: "number",
                        required: true,
                        placeholder: "Enter tenure",
                        label: "Tenure (days)",
                        initialValue: this.state.selectedTenure,
                        onNumberChange: this.handleTenureChange,
                        colConfig: { span: 11, offset: 1 },
                        elementStyle: { width: "100%" },
                      },
                      {
                        id: "assignedToId",
                        name: "assignedToId",
                        rule: "string",
                        inputType: "search",
                        required: true,
                        label: "Assign To",
                        placeholder: "Search and select advisor",
                        selectOptions: this.state.users,
                        changeHandler: this.handleUserSearchInput,
                        isSearching: this.state.isSearchingUser,
                        colConfig: { span: 24 },
                      },
                      {
                        id: "amountsPaid",
                        name: "amountsPaid",
                        inputType: "numberArray",
                        rule: "array",
                        required: true,
                        label: "Amounts Paid",
                        placeholder: "Enter amounts",
                        colConfig: { span: 24 },
                        elementStyle: { width: "100%" },
                        initialValue: this.state.amountsPaid,
                        onNumberArrayChange: (index: number, value: number) => {
                          const newAmountsPaid = [...this.state.amountsPaid];
                          newAmountsPaid[index] = value;
                          this.setState({ amountsPaid: newAmountsPaid });
                          this.paymentInstructionReference.current?.setFieldsValue(
                            {
                              amountsPaid: newAmountsPaid,
                            },
                          );
                        },
                        onAddNumberArrayItem: () => {
                          const newAmountsPaid = [...this.state.amountsPaid, 0];
                          this.setState({ amountsPaid: newAmountsPaid });
                          this.paymentInstructionReference.current?.setFieldsValue(
                            {
                              amountsPaid: newAmountsPaid,
                            },
                          );
                        },
                        onRemoveNumberArrayItem: (index: number) => {
                          if (this.state.amountsPaid.length > 1) {
                            const newAmountsPaid =
                              this.state.amountsPaid.filter(
                                (_, i) => i !== index,
                              );
                            this.setState({ amountsPaid: newAmountsPaid });
                            this.paymentInstructionReference.current?.setFieldsValue(
                              {
                                amountsPaid: newAmountsPaid,
                              },
                            );
                          }
                        },
                      },
                      {
                        name: "receiptUrl",
                        label: "Upload Payment Receipt",
                        inputType: "fileUpload",
                        defaultFileList: [],
                        placeholder: "Upload Payment Receipt",
                        rule: "string",
                        required: false,
                        changeHandler: (combinedInfo: string) =>
                          this.handleFileUploadChange(
                            "paymentInstructionReceiptUrl",
                            combinedInfo,
                          ),
                        colConfig: { span: 24 },
                      },
                    ]}
                  />
                ),
              })}
          </>
        </Card>
      </Spin>
    );
  }
}
